// 自身の所属するグループを常に保持する

import Vue from 'vue'
import lru from 'tiny-lru'
import API from '../../api'
import {callApiWithRefresh, timewait} from '../../api/util'

const cache = lru(30, 60000)
const state = {
    selected: null,
    ownGroups: {},
    joinedGroups: {},
    cached: {},
    accessToken: {},
    refreshToken: {}
}

const getters = {
    joinedGroupsSortByName: state => Object.keys(state.joinedGroups).sort((a_, b_) => {
        const a = state.joinedGroups[a_]
        const b = state.joinedGroups[b_]
        for(let i = 0; i < Math.min(a.length, b.length); i++) {
            const c_a = a.codePointAt(i)
            const c_b = b.codePointAt(i)
            if (c_a === c_b)
                continue
            else
                return c_a > c_b ? 1 : -1
        }
        if (a.length === b.length)
            return 0
        else
            return a.length > b.length ? 1 : -1
    }),
    defaultGroup: state => state.joinedGroups ? getters.joinedGroupsSortByName(state)[0] : null
    // defaultGroup: state => state.ownGroups ? Object.keys(state.ownGroups)[0] : null
}

const mutations = {
    _init(state, payload) {
        if (payload.joinedGroups !== undefined)
            state.joinedGroups = payload.joinedGroups
        if (payload.ownGroups !== undefined)
            state.ownGroups = payload.ownGroups
    },
    selectGroup(state, grpId) {
        state.selected = grpId
    },
    cached(state, group) {
        Vue.set(state.cached, group.GroupID, group)
    },
    deleteCache(state, gId) {
        Vue.set(state.cached, gId, undefined)
    },
    refreshGroupToken(state, {groupId, token}) {
        Vue.set(state.accessToken, groupId, token.access_token)
        Vue.set(state.refreshToken, groupId, token.refresh_token)
    },
}

const actions = {
    selectGroup(ctx, grpId) {
        if (grpId === undefined) // grpIdはnullはOK
            return Promise.resolve()
        return ctx.dispatch('getGroup', {gId: grpId})
        .then(() => {
            ctx.commit('selectGroup', grpId)
        })
    },
    unselectGroup(ctx) {
        return new Promise(resolve => {
            ctx.commit('selectGroup', null)
            resolve()
        })
    },
    registerGroup(ctx, name) {
        return callApiWithRefresh(
            () => ctx.rootState.account.accessToken,
            () => ctx.dispatch('account/_refreshToken', null, {root: true}),
            (token) => API.registerGroup(name, token)
        )
        .then(() => timewait(2000))
        .then(() => ctx.dispatch('account/getUser', null, {root: true}))
    },
    getGroup(ctx, {gId, opts={refresh: false}}) {
        return Promise.resolve()
        .then(() => {
            if (opts.refresh)
                cache.delete(gId)

            if (cache.get(gId))
                return Promise.resolve(ctx.state.cached[gId])
            else {
                return callApiWithRefresh(
                    () => ctx.state.accessToken[gId],
                    () => ctx.dispatch('_refreshToken', gId),
                    (token) => API.getGroup(token)
                )
                .then((group) => {
                    cache.set(gId, group)
                    ctx.commit('cached', group)
                    return group
                })
            }
        })
    },
    modifyGroup(ctx, {gId, name, state}) {
        return Promise.resolve()
        .then(() => {
            if (name) {
                return callApiWithRefresh(
                    () => ctx.rootState.account.accessToken,
                    () => ctx.dispatch('account/_refreshToken', null, {root: true}),
                    (token) => API.renameGroup(gId, name, token)
                )
            } else {
                return Promise.resolve()
            }
        })
        .then(() => {
            if (state) {
                return callApiWithRefresh(
                    () => ctx.rootState.account.accessToken,
                    () => ctx.dispatch('account/_refreshToken', null, {root: true}),
                    (token) => API.restateGroup(gId, state, token)
                )
            } else {
                return Promise.resolve()
            }
        })
        .then(() => timewait(2000))
        .then(() => Promise.all([
            ctx.dispatch('account/getUser', null, {root: true}),
            ctx.dispatch('getGroup', {gId: gId, opts: {refresh: true}})
        ]))
    },
    deleteGroup(ctx, gId) {
        return callApiWithRefresh(
            () => ctx.rootState.account.accessToken,
            () => ctx.dispatch('account/_refreshToken', null, {root: true}),
            (token) => API.deleteGroup(gId, token)
        )
        .then(() => timewait(2000))
        .then(() => ctx.dispatch('account/getUser', null, {root: true}))
        .then(() => ctx.commit('deleteCache', gId))
        .then(() => ctx.dispatch('groups/selectGroup', ctx.getters.defaultGroup, { root: true }))
    },
    inviteGroup(ctx, gId) {
        return callApiWithRefresh(
            () => ctx.state.accessToken[gId],
            () => ctx.dispatch('_refreshToken', gId),
            (token) => API.inviteGroup(token)
        )
    },
    joinGroup(ctx, cId) {
        return callApiWithRefresh(
            () => ctx.rootState.account.accessToken,
            () => ctx.dispatch('account/_refreshToken', null, {root: true}),
            (token) => API.joinGroup(cId, token)
        )
        .then(() => timewait(2000))
        .then(() => ctx.dispatch('account/getUser', null, {root: true}))
    },
    leaveGroup(ctx, gId) {
        return callApiWithRefresh(
            () => ctx.rootState.account.accessToken,
            () => ctx.dispatch('account/_refreshToken', null, {root: true}),
            (token) => API.leaveGroup(gId, token)
        )
        .then(() => timewait(2000))
        .then(() => ctx.dispatch('account/getUser', null, { root: true }))
        .then(() => ctx.commit('deleteCache', gId))
        .then(() => ctx.dispatch('groups/selectGroup', ctx.getters.defaultGroup, {root: true}))
    },
    _refreshToken(ctx, gId) {
        return new Promise((resolve) => {
            if (ctx.state.refreshToken[gId]) {
                API.getGroupToken(gId, ctx.state.refreshToken[gId])
                .then(gToken => {
                    ctx.commit('refreshGroupToken', {groupId: gId, token: gToken})
                    resolve()
                })
            } else {
                API.getGroupToken(gId, ctx.rootState.account.refreshToken)
                .then(gToken => {
                    ctx.commit('refreshGroupToken', {groupId: gId, token: gToken})
                    resolve()
                })
            }
        })
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
